import React, { PureComponent } from 'react';
import * as d3 from 'd3';
import { withWindow, WindowManager } from 'react-window-decorators';
import Waypoint from 'react-waypoint';
import ReactGA from 'react-ga';
import ApplicationsVisualization from './ApplicationsVisualization';
import ApplicationInfo from './ApplicationInfo';
import ApplicationsToHighlight from './ApplicationsToHighlight';
import './App.css';

new WindowManager();

// Number of school columns in the application data
const MAX_SCHOOLS = 92;

const STEPS_ENUM = {
  BEGIN_EXPLANATION: 1,
  SHOW_MINI_APPLICATIONS: 2,
  SHOW_ASSIGNED_SCHOOLS: 3,
  TRANSITION_APPLICATIONS_TO_CIRCLES: 4,
  SHOW_ALL_CIRCLES: 5,
  HIDE_SMALLEST_CIRCLES: 6,
  TRANSITION_TO_SIMILARITY_LAYOUT: 7,
  END_EXPLANATION: 8
};

class App extends PureComponent {

  state = {
    applications: [],
    browsableIDs: [],
    schoolCodes: new Map(),
    selectedAssignmentStatus: null,
    selectedEnrollmentStatus: null,
    selectedCTIPStatus: null,
    selectedSchool: null,
    selectedZip: null,
    clickedApplication: null,
    step: STEPS_ENUM.BEGIN_EXPLANATION,
    drawWidth: 0,
    drawHeight: 0
  }

  constructor(props) {
    super(props);
    ReactGA.initialize('UA-127764172-1');
  }

  componentDidMount() {
    // Fetch data files from the public folder
    Promise.all([
      d3.csv(process.env.PUBLIC_URL + '/data/kqed_data.csv'),
      d3.csv(process.env.PUBLIC_URL + '/data/tsne_p90.csv'),
      d3.csv(process.env.PUBLIC_URL + '/data/school_codes.csv'),
      d3.csv(process.env.PUBLIC_URL + '/data/zip_codes.csv')
    ]).then(([kqed_data, tsne_p90, school_codes, zip_codes]) => {
      //console.log(kqed_data, tsne_p90, school_codes, zip_codes);
      const tsne = this.processPositions(tsne_p90);
      const schoolCodes = this.processSchoolCodes(school_codes);
      const zipCodes = this.processZipCodes(zip_codes);
      // Overall, exclude people who missed the first round (were not assigned a school) and whose zip is from outside SF
      const applications = this.processApplications(kqed_data, tsne, schoolCodes, zipCodes).filter(d => d.assigned && d.zip.startsWith('941'));
      // Establish a subset of IDs that we will make browsable in the last part of the visualization
      const browsableIDs = this.identifyBrowsableSubset(applications);
      //console.log(applications);
      //console.log(browsableIDs);
      this.setState({ applications, browsableIDs, schoolCodes, drawWidth: this.calculateDrawWidth(), drawHeight: this.calculateDrawHeight() });
    });
    ReactGA.pageview('/');
  }

  calculateDrawWidth() {
    if (this.props.orientation === 'portrait') {
      return this.props.dimensions.width - 100;
    } else if (this.props.dimensions.width > 850) {
      return this.props.dimensions.width - 500;
    } else {
      return this.props.dimensions.width - 400;
    }
  }

  calculateDrawHeight() {
    if (this.props.orientation === 'portrait') {
      return this.props.dimensions.height - 325;
    } else {
      return this.props.dimensions.height - 75;
    }
  }

  processApplications(csv, tsne, schoolCodes, zipCodes) {
    return csv.map((row) => {
      const schools = [];
      const assigned = row["Round 1 Assignment"];
      const enrolled = row["School Enrolled In As Of 11/03/2017"];
      let assignedRank = 0;
      let enrolledRank = 0;
      for (let i = 1; i <= MAX_SCHOOLS; i++) {
        const school = row[i.toString()];
        if (school && school !== "") {
          schools.push(school);
          if (assignedRank === 0 && school === assigned) {
            assignedRank = i;
          }
          if (enrolledRank === 0 && school === enrolled) {
            enrolledRank = i;
          }
        }
        // TODO: for efficiency, break after first empty string is detected?
        // Depends on whether we ever get empty strings earlier in the list.
      }

      return {
        id: row["StudentNo"],
        schools: schools,
        applications: schools.length,
        assigned: assigned,
        assignedRank: assignedRank,
        assigned1stChoice: assignedRank === 1 ? "Yes" : "No",
        enrolled: enrolled,
        enrolledRank: enrolledRank,
        enrolledStatus: schoolCodes.has(enrolled) && schoolCodes.get(enrolled).sfusd ? (assignedRank === enrolledRank ? "Assigned school" : "Other SFUSD") : "Outside SFUSD",
        ctip: row["Does Student Live In CTIP1 Zone? (y/n)"] === "Y" ? "Yes" : "No",
        zip: zipCodes.has(row["Student's Residential Zip Code"]) ? zipCodes.get(row["Student's Residential Zip Code"]) : "Other",
        tsne: tsne.get(row["StudentNo"])
      };
    });
  }

  processPositions(csv) {
    const positions = new Map();
    csv.forEach(function (row) {
      const id = row["StudentNo"];
      const position = { x: +row.x, y: +row.y };
      positions.set(id, position);
    });
    return positions;
  }

  processSchoolCodes(csv) {
    const schoolCodes = new Map();
    csv.forEach(function (row) {
      const code = row["School #"];
      const info = { name: row["School Name"], citywide: row["Citywide"] === "yes", sfusd: row["Type"] === "SFUSD" };
      schoolCodes.set(code, info);
    });
    return schoolCodes;
  }

  processZipCodes(csv) {
    const zipCodesCombined = {
      "94102": "94102, 94103: Hayes Valley, SOMA",
      "94103": "94102, 94103: Hayes Valley, SOMA",
      "94104": "94104, 94105, 94108, 94111, 94130, 94133: Northeast SF",
      "94105": "94104, 94105, 94108, 94111, 94130, 94133: Northeast SF",
      "94108": "94104, 94105, 94108, 94111, 94130, 94133: Northeast SF",
      "94111": "94104, 94105, 94108, 94111, 94130, 94133: Northeast SF",
      "94130": "94104, 94105, 94108, 94111, 94130, 94133: Northeast SF",
      "94133": "94104, 94105, 94108, 94111, 94130, 94133: Northeast SF",
      "94123": "94123, 94129: Marina, Presidio",
      "94129": "94123, 94129: Marina, Presidio",
      "94107": "94107, 94158: Potrero, Mission Bay",
      "94158": "94107, 94158: Potrero, Mission Bay"
    };
    const zipCodes = new Map();
    csv.forEach(function (row) {
      const zip = row["Zip code"];
      let description = zip + ": " + row["Description"];
      if (zipCodesCombined[zip]) {
        description = zipCodesCombined[zip]
      }
      zipCodes.set(zip, description);
    });
    return zipCodes;
  }

  identifyBrowsableSubset(applications) {
    const extrasheet = applications.filter(d => d.applications > 11);
    const uniqueApplications = new Map();
    // Identify applications that have the same list of schools, the same assigned and enrolled schools, and the same demographics;
    // assume these are twins and retain only one of the applications for visualization purposes.
    extrasheet.forEach((d) => {
      const signature = [...d.schools, d.assigned, d.enrolled, d.zip, d.ctip].join();
      uniqueApplications.set(signature, d.id);
    });
    return new Set([...uniqueApplications.values()]);
  }

  componentDidUpdate(prevProps) {
    // Note: using componentDidUpdate instead of getDerivedStateFromProps avoids having to put breakpoint and orientation in state
    // Don't resize in response to very small changes in width or height
    const drawWidth = this.calculateDrawWidth();
    const drawHeight = this.calculateDrawHeight();
    if (prevProps.orientation !== this.props.orientation ||
      Math.abs(drawWidth - this.state.drawWidth) > 50 ||
      Math.abs(drawHeight - this.state.drawHeight) > 100) {
      this.setState({ drawWidth, drawHeight });
    }
  }

  // These must be arrow functions so that "this" behaves as expected
  handleClick = (d) => {
    if (this.state.clickedApplication && this.state.clickedApplication.id === d.data.id) {
      this.setState({ clickedApplication: null });
      ReactGA.event({
        category: 'Interaction',
        action: 'Hide application'
      });
    } else {
      this.setState({ clickedApplication: d.data });
      ReactGA.event({
        category: 'Interaction',
        action: 'Show application',
        label: d.data.id
      });
    }
  }

  handleDropdownChange = (key, value) => {
    if (value && !value.value) return;
    const newState = {};
    newState[key] = value;
    this.setState(newState);
    ReactGA.event({
      category: 'Filter',
      action: key,
      label: value ? value.label : ""
    });
  }

  resetControls = () => {
    this.setState({
      selectedAssignmentStatus: null,
      selectedEnrollmentStatus: null,
      selectedCTIPStatus: null,
      selectedSchool: null,
      selectedZip: null
    });
    ReactGA.event({
      category: 'Filter',
      action: 'Clear all'
    });
  }

  changeStep = (step) => {
    this.setState({ step });
    ReactGA.pageview('/step' + step);
  }

  render() {
    const filteredApplicationPercentage = d3.format('.0%')(this.state.applications.filter(d => d.applications > 11).length / this.state.applications.length);
    return (
      <div className="app">
        <div id="intro" className="part">
          <h1>Exploring San Francisco’s kindergarten lottery applications</h1>
          <p>San Francisco uses a lottery system to assign children to public schools. The system is controversial—trying to balance
            parent choice with reducing segregation—and the process is opaque and poorly understood.</p>
          <p>This visualization aims to offer some transparency to prospective applicants, by making it possible to browse real anonymized applications
            (and outcomes) from the 2017 process.</p>
          <p className="scroll">Scroll to continue ↓</p>
        </div>

        <ApplicationsVisualization
          applications={this.state.applications}
          browsableIDs={this.state.browsableIDs}
          schoolCodes={this.state.schoolCodes}
          selectedAssignmentStatus={this.state.selectedAssignmentStatus}
          selectedEnrollmentStatus={this.state.selectedEnrollmentStatus}
          selectedCTIPStatus={this.state.selectedCTIPStatus}
          selectedSchool={this.state.selectedSchool}
          selectedZip={this.state.selectedZip}
          step={this.state.step}
          stepsEnum={STEPS_ENUM}
          drawWidth={this.state.drawWidth}
          drawHeight={this.state.drawHeight}
          handleClick={this.handleClick}
          clicked={this.state.clickedApplication ? this.state.clickedApplication.id : null}
        />

        <div className={this.state.step >= STEPS_ENUM.END_EXPLANATION ? "explanation shifted" : "explanation"}>
          <Waypoint onEnter={() => this.changeStep(STEPS_ENUM.SHOW_MINI_APPLICATIONS)}>
            <div className="part">
              <p>When parents submit their application for kindergarten, they fill in a paper form where they are asked to rank their list of
              schools in order of preference.</p>
            </div>
          </Waypoint>
          <Waypoint onEnter={() => this.changeStep(STEPS_ENUM.SHOW_ASSIGNED_SCHOOLS)}>
            <div className="part">
              <p>A separate lottery is then run for each school, with all applicants who named that school anywhere on their list.</p>
              <p>Each applicant is then assigned to the highest school on their list that accepted them.</p>
            </div>
          </Waypoint>
          <Waypoint onEnter={() => this.changeStep(STEPS_ENUM.TRANSITION_APPLICATIONS_TO_CIRCLES)}>
            <div className="part">
              <p>In this visualization, we’ll represent each application as a circle. The size of each circle corresponds to the number of schools
              that were listed.</p>
              <p>Official SFUSD
              advice is to rank “as many schools as possible that work for you” but informal strategies have suggested listing many more.</p>
            </div>
          </Waypoint>
          <Waypoint onEnter={() => this.changeStep(STEPS_ENUM.SHOW_ALL_CIRCLES)}>
            <div className="part">
              <p>Now, we’re seeing each of the {this.state.applications.length} applications for kindergarten in San Francisco
                in 2017.</p>
              <p>The application form has room to list 10 schools, and parents who want to name more than 10 must submit an extra sheet of paper.
                In 2017, {filteredApplicationPercentage} of parents went to the trouble of doing that.</p>
              <p>In the rest of the visualization, we’ll focus on these long applications and remove the others.</p>
            </div>
          </Waypoint>
          <Waypoint onEnter={() => this.changeStep(STEPS_ENUM.HIDE_SMALLEST_CIRCLES)}>
            <div className="part">
              <p>Next, we’re going to arrange the remaining applications according to the similarity of their ranked list of schools, to make it easier to
              look for patterns.</p>
            </div>
          </Waypoint>
          <Waypoint onEnter={() => this.changeStep(STEPS_ENUM.TRANSITION_TO_SIMILARITY_LAYOUT)}>
            <div className="part">
              <p>The resulting clusters are roughly based on each applicant's first-choice school; we’ve labelled the most popular ones.</p>
            </div>
          </Waypoint>
          <Waypoint onEnter={() => this.changeStep(STEPS_ENUM.TRANSITION_TO_SIMILARITY_LAYOUT)}>
            <div className="part">
              <p>Next, you can explore the data for yourself: scroll to the end, then click on any circle to see the corresponding application and its outcome.</p>
              <p>Remember that this is only a subset of applications, and they are not representative of the whole set, or intended as a model.</p>
            </div>
          </Waypoint>
          <Waypoint onEnter={() => this.changeStep(STEPS_ENUM.END_EXPLANATION)}>
            <div className="backstop"></div>
          </Waypoint>
        </div>
        <div className={this.state.step >= STEPS_ENUM.END_EXPLANATION ? "side-panel onscreen" : "side-panel"}>
          <header>
            <ApplicationsToHighlight
              applications={this.state.applications}
              browsableIDs={this.state.browsableIDs}
              schoolCodes={this.state.schoolCodes}
              selectedAssignmentStatus={this.state.selectedAssignmentStatus}
              selectedEnrollmentStatus={this.state.selectedEnrollmentStatus}
              selectedCTIPStatus={this.state.selectedCTIPStatus}
              selectedSchool={this.state.selectedSchool}
              selectedZip={this.state.selectedZip}
              onDropdownChange={this.handleDropdownChange}
              resetControls={this.resetControls}
            />
          </header>
          <main>
            <ApplicationInfo
              application={this.state.clickedApplication}
              schoolCodes={this.state.schoolCodes}
            />
          </main>
          <footer>
            <span className="size-disclaimer">Note: Use a wider screen for more search options.</span><br />
            <div className="faq">
              <svg width={25} height={20}>
                <circle cx={10} cy={10} r={8}></circle>
                <text x={10} y={10} dy="0.35em" textAnchor="middle">?</text>
              </svg>
              <ReactGA.OutboundLink
                eventLabel="FAQ"
                to="https://docs.google.com/document/d/1OWrZdQ8S3zph5f_PIxMoT2qH5d2Y1H_M3FCE4bNohhg/edit?usp=sharing"
                target="_blank"
                rel="noopener">
                FAQ and caveats
            </ReactGA.OutboundLink>
            </div>
          </footer>
        </div>
      </div >
    );
  }
}

export default withWindow(App);



// WEBPACK FOOTER //
// ./src/App.js